// Admin navigation config — single source of truth.
// Consumed by AdminSidebar, AdminTabBar, CommandPalette (Phase 1).

// lucide-react icon name strings (resolved to components at render site).
// Keeping them as strings here avoids pulling lucide into server config
// and lets tests assert structure without JSX.

import { MEMBERS_ENABLED } from "@/lib/featureFlags";

export const NAV_GROUPS = {
  main: { label: "主要", order: 1 },
  artworks: { label: "作品管理", order: 2 },
  orders: { label: "訂單", order: 3 },
  finance: { label: "金流", order: 4 },
  site: { label: "網站", order: 5 },
  tools: { label: "工具", order: 6 },
};

export const ADMIN_NAV_ITEMS = [
  {
    id: "dashboard",
    label: "首頁",
    href: "/admin",
    icon: "LayoutDashboard",
    group: "main",
    mobileTab: true,
    mobileTabOrder: 1,
    keywords: ["dashboard", "home", "overview", "首頁", "總覽"],
  },
  {
    id: "members",
    label: "會員",
    href: "/admin/members",
    icon: "Users",
    group: "main",
    memberOnly: true,
    keywords: ["members", "users", "customer", "會員", "顧客", "客戶"],
  },
  {
    id: "password",
    label: "修改密碼",
    href: "/admin/password",
    icon: "KeyRound",
    group: "main",
    keywords: [
      "password",
      "security",
      "credentials",
      "修改密碼",
      "密碼",
      "安全",
      "更改密碼",
    ],
  },

  {
    id: "upload",
    label: "上傳新作品",
    href: "/admin/upload",
    icon: "Upload",
    group: "artworks",
    mobileTab: true,
    mobileTabOrder: 2,
    keywords: ["upload", "new", "add", "上傳新作品", "上傳", "新增", "作品"],
  },
  {
    id: "artworks",
    label: "管理作品",
    href: "/admin/artworks",
    icon: "Images",
    group: "artworks",
    keywords: [
      "artworks",
      "images",
      "manage",
      "gallery",
      "管理作品",
      "作品",
      "圖檔",
      "管理",
      "圖庫",
    ],
  },
  {
    id: "visibility",
    label: "審核顯示",
    href: "/admin/visibility",
    icon: "Eye",
    group: "artworks",
    keywords: [
      "visibility",
      "review",
      "approve",
      "hide",
      "show",
      "審核顯示",
      "審核",
      "顯示",
      "隱藏",
      "公開",
    ],
  },

  {
    id: "orders",
    label: "圖檔訂單",
    href: "/admin/orders",
    icon: "Receipt",
    group: "orders",
    mobileTab: true,
    mobileTabOrder: 3,
    keywords: [
      "orders",
      "purchases",
      "sales",
      "圖檔訂單",
      "訂單",
      "銷售",
      "購買",
    ],
  },
  {
    id: "sticker-orders",
    label: "貼紙訂單",
    href: "/admin/sticker-orders",
    icon: "Package",
    group: "orders",
    keywords: [
      "stickers",
      "sticker-orders",
      "shipping",
      "貼紙訂單",
      "貼紙",
      "出貨",
    ],
  },

  {
    id: "payment-events",
    label: "付款紀錄",
    href: "/admin/payment-events",
    icon: "CreditCard",
    group: "finance",
    keywords: [
      "payment",
      "events",
      "webhook",
      "ecpay",
      "付款紀錄",
      "付款",
      "事件",
      "綠界",
    ],
  },
  {
    id: "reconciliation",
    label: "每日對帳",
    href: "/admin/reconciliation",
    icon: "BookOpenCheck",
    group: "finance",
    keywords: [
      "reconciliation",
      "daily",
      "reconcile",
      "diff",
      "每日對帳",
      "對帳",
      "核對",
      "差異",
    ],
  },

  {
    id: "content",
    label: "網頁內容",
    href: "/admin/content",
    icon: "FileText",
    group: "site",
    keywords: [
      "content",
      "cms",
      "pages",
      "text",
      "網頁內容",
      "內容",
      "網頁",
      "文字",
    ],
  },
  {
    id: "settings",
    label: "全站設定",
    href: "/admin/settings",
    icon: "Settings",
    group: "site",
    keywords: [
      "settings",
      "config",
      "brand",
      "seo",
      "全站設定",
      "設定",
      "品牌",
    ],
  },
  {
    id: "songs",
    label: "歌曲藝廊",
    href: "/admin/songs",
    icon: "Music",
    group: "site",
    keywords: ["songs", "music", "gallery", "歌曲藝廊", "歌曲", "音樂", "藝廊"],
  },
  {
    id: "blog",
    label: "文章",
    href: "/admin/blog",
    icon: "FileText",
    group: "site",
    keywords: ["blog", "post", "article", "文章", "教學", "部落格"],
  },
  {
    id: "retail-stores",
    label: "寄賣點",
    href: "/admin/retail-stores",
    icon: "Store",
    group: "site",
    keywords: [
      "retail",
      "stores",
      "locations",
      "寄賣點",
      "寄賣",
      "店家",
      "地點",
    ],
  },

  {
    id: "trash",
    label: "回收站",
    href: "/admin/trash",
    icon: "Trash2",
    group: "tools",
    tourId: "trash",
    keywords: [
      "trash",
      "deleted",
      "recycle",
      "restore",
      "回收站",
      "回收",
      "刪除",
      "還原",
    ],
  },
];

// Mobile bottom-tab bar: exactly 4 slots. First 3 are the most-used pages;
// slot 4 is the "more" opener which triggers a bottom-sheet listing all other items.
const MORE_PLACEHOLDER = {
  id: "more",
  label: "更多",
  href: null, // opens sheet, not a route
  icon: "MoreHorizontal",
  group: "main",
  mobileTab: true,
  mobileTabOrder: 4,
};

// Hide member-system-only items when the feature flag is off (default).
const visibleItems = () =>
  ADMIN_NAV_ITEMS.filter((i) => !i.memberOnly || MEMBERS_ENABLED);

export function getMobileTabItems() {
  const tabs = visibleItems().filter(
    (i) => i.mobileTab && i.mobileTabOrder < 4,
  );
  return [...tabs, MORE_PLACEHOLDER].sort(
    (a, b) => a.mobileTabOrder - b.mobileTabOrder,
  );
}

export function getItemsByGroup() {
  const groupIds = Object.keys(NAV_GROUPS).sort(
    (a, b) => NAV_GROUPS[a].order - NAV_GROUPS[b].order,
  );
  return groupIds.map((groupId) => ({
    groupId,
    label: NAV_GROUPS[groupId].label,
    items: visibleItems().filter((i) => i.group === groupId),
  }));
}
